/**
 * "Other Statistics" — the band under Occupancy where any of the property's
 * stat accounts (covers, guests, comp rooms, labor hours, kWh… every non-P&L
 * stat-typed chart account that is NOT an Occupied/Available Rooms segment)
 * gets a plan per month. Each row can carry a driver (LY actual, TTM, budget,
 * seasonal spread, per occupied/available room, revenue ÷ $ per unit — see
 * azfApi/utils/forecastStatDrivers.js); a keyed cell overrides the driver for
 * that month, the money rows' rule. Publish writes keyed ?? driven to
 * financialBudget / financialForecast next to the money rows.
 *
 * Own data channel (getStatValues / saveStatValues / saveStatDriver) —
 * deliberately outside calculateForecast so stat counts never fall into the
 * Revenue-else-Expense buckets that feed department totals, GOP and Net
 * Income. The on-screen room-night and revenue projections are sent along so
 * the server drives stats off the same numbers the grid shows.
 *
 * Entry: click a month, type, Enter (commit + down a row) / Tab (commit +
 * next month) / Escape. Pasting a spreadsheet row fills the months to the
 * right of the cell in one save. Blank clears the override (back to the driver).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Sliders } from 'lucide-react';
import toast from 'react-hot-toast';
import { getStatValues, saveStatDriver, saveStatValues } from '../../api';
import type { StatDriverConfig, StatValueItem, StatValuesResponse } from '../../api';
import type { ForecastAccount } from '../../types';
import { MONTHS, MONTH_LABELS } from '../../types';
import {
  buildStatProjected, buildStatRows, describeStatDriver, formatStatNumber, parsePastedValues, parseStatInput, statColumnTotal, statColumnValue,
} from '../../utils/otherStatsGrid';
import type { StatRow } from '../../utils/otherStatsGrid';
import type { ExpandedColumnDef } from './columnRegistry';
import { TOTAL_COLUMN_INDEX } from './columnRegistry';
import { publishOtherStats, selectStatAccount, useSelectedStatAccountId } from './otherStatsSession';

export const otherStatsQueryKey = (
  orgId: number | null | undefined,
  locationId: number | null | undefined,
  versionId: number | string | null | undefined,
  year: number | undefined,
  projectedKey: string,
) => ['forecast-v3', 'statValues', orgId ?? null, locationId ?? null, versionId ?? null, year ?? null, projectedKey] as const;

export interface OtherStatsSectionProps {
  orgId?: number | null;
  locationId?: number | null;
  versionId?: number | string | null;
  year?: number;
  isBulkMode?: boolean;
  isColumnExpanded?: (index: number) => boolean;
  activeColumns?: ExpandedColumnDef[];
  activeColumnCount?: number;
  expandedSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
  actualRollInEnabled?: boolean;
  closedMonthSet?: Set<string>;
  isBudget?: boolean;
  isReadOnly?: boolean;
  /** Resolved room-night projections on screen (the Occupancy rows). */
  roomStats?: { occupiedRooms: Record<string, number>; availableRooms: Record<string, number> } | null;
  /** Revenue accounts with their on-screen monthly values (per_revenue basis + picker). */
  revenueAccounts?: ForecastAccount[];
}

const SECTION_KEY = 'otherStats';

export function OtherStatsSection({
  orgId,
  locationId,
  versionId,
  year,
  isBulkMode = false,
  isColumnExpanded,
  activeColumns = [],
  activeColumnCount = 7,
  expandedSections,
  toggleSection,
  actualRollInEnabled = false,
  closedMonthSet,
  isBudget = false,
  isReadOnly = false,
  roomStats = null,
  revenueAccounts = [],
}: OtherStatsSectionProps) {
  const queryClient = useQueryClient();
  const isExpanded = expandedSections[SECTION_KEY] === true;
  const totalColSpan = isColumnExpanded?.(TOTAL_COLUMN_INDEX) ? Math.max(1, activeColumnCount) : 1;
  const canFetch = !!orgId && !!locationId && !!year;

  // What the drivers multiply against — sent with the fetch, and folded into
  // the query key so a recalculated forecast re-drives the statistics.
  const projected = useMemo(() => buildStatProjected(roomStats, revenueAccounts), [roomStats, revenueAccounts]);
  const projectedKey = useMemo(() => {
    if (!projected) return 'none';
    const sum = (o: Record<string, number>) => Object.values(o).reduce((s, v) => s + (Number(v) || 0), 0);
    const rev = Object.values(projected.revenue).reduce((s, months) => s + sum(months), 0);
    return `${Math.round(sum(projected.occupiedRooms))}|${Math.round(sum(projected.availableRooms))}|${Math.round(rev)}`;
  }, [projected]);
  const queryKey = otherStatsQueryKey(orgId, locationId, versionId, year, projectedKey);
  const invalidateAll = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['forecast-v3', 'statValues', orgId ?? null, locationId ?? null, versionId ?? null, year ?? null] }),
    [queryClient, orgId, locationId, versionId, year]
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: () => getStatValues({ orgId: orgId as number, locationId: locationId as number, versionId, year: year as number, projected }),
    enabled: canFetch && isExpanded,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  // Actual months mirror the money rows: closed month + roll-in on, never on budgets.
  const actualMonthKeys = useMemo(
    () => new Set<string>(actualRollInEnabled && !isBudget && closedMonthSet ? MONTHS.filter((m) => closedMonthSet.has(m)) : []),
    [actualRollInEnabled, isBudget, closedMonthSet]
  );

  const rows: StatRow[] = useMemo(() => {
    if (!data) return [];
    return buildStatRows({
      accounts: data.accounts,
      values: data.values || {},
      driven: data.driven || {},
      notes: data.notes || {},
      actuals: data.actuals || {},
      lyActuals: data.lyActuals || {},
      budget: data.budget || {},
      lyBudget: data.lyBudget || {},
      actualMonthKeys,
    });
  }, [data, actualMonthKeys]);
  const driverDefs = useMemo(() => data?.drivers || [], [data]);
  const driverLabel = useCallback((d: StatDriverConfig | null | undefined) => driverDefs.find((x) => x.id === d?.driverType)?.label, [driverDefs]);

  const canEdit = !isReadOnly && !!versionId && data?.tableReady !== false;
  const planWord = isBudget ? 'budget' : 'forecast';

  // ── detail pane (the standard right-hand pane, via otherStatsSession) ────
  const selectedStatId = useSelectedStatAccountId();
  const handleSaveDriver = useCallback(async (row: StatRow, config: StatDriverConfig) => {
    if (!versionId) return;
    await saveStatDriver({ versionId, chartOfAccountId: row.account.chartOfAccountId, ...config });
    toast.success(config.driverType === 'manual' ? `${row.account.accountName}: manual entry` : `${row.account.accountName}: ${driverLabel(config) || config.driverType}`);
    await invalidateAll();
  }, [versionId, driverLabel, invalidateAll]);

  // ── cell editing ─────────────────────────────────────────────────────────
  const [editing, setEditing] = useState<{ rowIdx: number; monthIdx: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  // Commit-and-move sets the next cell synchronously; blur from the unmounting
  // input must not commit a second time.
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    // Whether or not the previous input fired blur on unmount, the new cell
    // starts with a live blur handler.
    skipBlurRef.current = false;
  }, [editing]);

  const startEdit = useCallback((rowIdx: number, monthIdx: number) => {
    if (!canEdit) return;
    const cell = rows[rowIdx]?.cells[monthIdx];
    setEditing({ rowIdx, monthIdx });
    // Seed with the plan (keyed or driven) so a driven month can be nudged.
    setEditValue(cell?.plan === null || cell?.plan === undefined ? '' : String(cell.plan));
  }, [canEdit, rows]);

  /** Persist items; optimistic local update so the grid doesn't wait on the refetch. */
  const persist = useCallback(async (items: StatValueItem[]) => {
    if (!versionId || items.length === 0) return;
    const keys = items.map((it) => `${it.chartOfAccountId}-${it.month}`);
    setSaving((prev) => { const next = new Set(prev); keys.forEach((k) => next.add(k)); return next; });
    try {
      const res = await saveStatValues({ versionId, items });
      queryClient.setQueryData<StatValuesResponse | undefined>(queryKey, (prev) => {
        if (!prev) return prev;
        const values: StatValuesResponse['values'] = { ...prev.values };
        const locked = new Set((res.skippedLocked || []).map((s) => `${s.chartOfAccountId}-${s.month}`));
        for (const it of items) {
          if (locked.has(`${it.chartOfAccountId}-${it.month}`)) continue;
          const acct = { ...(values[it.chartOfAccountId] || {}) };
          if (it.value === null) delete acct[it.month];
          else acct[it.month] = it.value;
          values[it.chartOfAccountId] = acct;
        }
        return { ...prev, values };
      });
      if (res.skippedLocked?.length) {
        toast.error(`${res.skippedLocked.length} locked month${res.skippedLocked.length === 1 ? '' : 's'} left unchanged`);
      }
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to save statistic';
      toast.error(message);
      throw err;
    } finally {
      setSaving((prev) => { const next = new Set(prev); keys.forEach((k) => next.delete(k)); return next; });
      queryClient.invalidateQueries({ queryKey });
    }
  }, [versionId, queryClient, queryKey]);

  const commitEdit = useCallback(async (): Promise<boolean> => {
    if (!editing || !year) return true;
    const row = rows[editing.rowIdx];
    if (!row) return true;
    const parsed = parseStatInput(editValue);
    if ('error' in parsed) {
      toast.error(parsed.error);
      return false;
    }
    const cell = row.cells[editing.monthIdx];
    const keyed = cell?.keyed ?? null;
    // Typing the driven value back onto an un-keyed month is not an override.
    const target = parsed.value !== null && keyed === null && cell?.driven !== null && parsed.value === cell.driven ? null : parsed.value;
    if (target === keyed) return true;
    try {
      await persist([{ chartOfAccountId: row.account.chartOfAccountId, year, month: editing.monthIdx + 1, value: target }]);
      return true;
    } catch {
      return false;
    }
  }, [editing, editValue, persist, rows, year]);

  const moveEdit = useCallback((dRow: number, dMonth: number) => {
    if (!editing) return;
    const nextMonth = editing.monthIdx + dMonth;
    const nextRow = editing.rowIdx + dRow;
    if (nextMonth < 0 || nextMonth >= MONTHS.length || nextRow < 0 || nextRow >= rows.length) {
      setEditing(null);
      return;
    }
    startEdit(nextRow, nextMonth);
  }, [editing, rows.length, startEdit]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      skipBlurRef.current = true;
      setEditing(null);
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      skipBlurRef.current = true;
      const ok = await commitEdit();
      if (!ok) { skipBlurRef.current = false; return; }
      if (e.key === 'Enter') moveEdit(e.shiftKey ? -1 : 1, 0);
      else moveEdit(0, e.shiftKey ? -1 : 1);
    }
  };

  const handleBlur = async () => {
    if (skipBlurRef.current) { skipBlurRef.current = false; return; }
    const ok = await commitEdit();
    if (ok) setEditing(null);
    else inputRef.current?.focus();
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!editing || !year) return;
    const text = e.clipboardData.getData('text');
    const parsed = parsePastedValues(text);
    if (parsed.length <= 1) return; // single value: let the input take it
    e.preventDefault();
    const row = rows[editing.rowIdx];
    if (!row) return;
    const items: StatValueItem[] = [];
    parsed.forEach((value, offset) => {
      const monthIdx = editing.monthIdx + offset;
      if (value === undefined || monthIdx >= MONTHS.length) return;
      items.push({ chartOfAccountId: row.account.chartOfAccountId, year, month: monthIdx + 1, value });
    });
    skipBlurRef.current = true;
    setEditing(null);
    try {
      await persist(items);
      toast.success(`${items.length} month${items.length === 1 ? '' : 's'} pasted`);
    } catch { /* toast already shown */ }
  };

  const clearKeyed = async (row: StatRow, monthIdx?: number) => {
    if (!year) return;
    const items: StatValueItem[] = [];
    row.cells.forEach((cell, idx) => {
      if (cell.keyed !== null && (monthIdx === undefined || idx === monthIdx)) {
        items.push({ chartOfAccountId: row.account.chartOfAccountId, year, month: idx + 1, value: null });
      }
    });
    if (items.length === 0) return;
    try {
      await persist(items);
      toast.success(monthIdx === undefined ? `${row.account.accountName}: keyed values cleared` : 'Back to the driver value');
    } catch { /* toast already shown */ }
  };

  // The pane reads rows + handlers from here; it never fetches on its own.
  useEffect(() => {
    if (!isExpanded) return;
    publishOtherStats({ rows, driverDefs, revenueAccounts, canEdit, isBudget, year, saveDriver: handleSaveDriver, saveKeyed: persist });
  }, [isExpanded, rows, driverDefs, revenueAccounts, canEdit, isBudget, year, handleSaveDriver, persist]);
  useEffect(() => () => { publishOtherStats(null); selectStatAccount(null); }, []);
  // Collapsing the band closes its pane.
  useEffect(() => { if (!isExpanded) selectStatAccount(null); }, [isExpanded]);

  // ── rendering ────────────────────────────────────────────────────────────
  const keyedAccounts = rows.filter((r) => r.hasKeyed).length;
  const drivenAccounts = rows.filter((r) => r.hasDriver).length;
  const stickyLabelClass = `${isBulkMode ? '' : 'sticky left-0'} bg-white py-1 px-4 pl-8 text-xs text-slate-600 z-20 group-hover:bg-slate-50`;

  const renderInput = (row: StatRow, monthIdx: number) => {
    const cell = row.cells[monthIdx];
    const showReset = cell.keyed !== null && row.hasDriver;
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="w-full text-right bg-white rounded px-1 py-0.5 text-xs focus:outline-none"
          style={{ border: '1px solid var(--lodge-orange-300)' }}
        />
        {showReset && (
          <button
            onMouseDown={(e) => { e.preventDefault(); skipBlurRef.current = true; setEditing(null); clearKeyed(row, monthIdx); }}
            className="flex-shrink-0 px-1 py-0.5 text-[9px] font-medium rounded hover:bg-red-50 transition-colors whitespace-nowrap"
            style={{ color: 'var(--lodge-orange-500)', border: '1px solid var(--lodge-orange-200)' }}
            title="Clear the keyed value and go back to the driver"
          >
            Reset
          </button>
        )}
      </div>
    );
  };

  const cellTitle = (row: StatRow, monthIdx: number) => {
    const cell = row.cells[monthIdx];
    const parts: string[] = [];
    if (cell.source === 'actual') parts.push(`Recorded actual${cell.plan !== null ? ` · ${planWord} ${formatStatNumber(cell.plan)}` : ''}`);
    else if (cell.source === 'keyed') parts.push(row.hasDriver ? `Keyed override${cell.driven !== null ? ` · driver ${formatStatNumber(cell.driven)} (${cell.note})` : ''}` : `Keyed ${planWord}`);
    else if (cell.source === 'driven') parts.push(cell.note || 'Driver value');
    else if (row.hasDriver && cell.note) parts.push(cell.note);
    if (cell.budget !== null && !isBudget) parts.push(`Budget ${formatStatNumber(cell.budget)}`);
    if (cell.lyActual !== null) parts.push(`LY ${formatStatNumber(cell.lyActual)}`);
    if (canEdit) parts.push('Click to enter · Tab moves right · paste a row to fill');
    return parts.join(' · ');
  };

  const valueClass = (cell: StatRow['cells'][number]) =>
    cell.source === 'keyed' ? 'font-medium' : cell.source === 'driven' ? 'text-slate-600' : cell.source === 'actual' ? 'text-slate-500' : 'text-slate-600';
  const valueStyle = (cell: StatRow['cells'][number], row: StatRow): React.CSSProperties | undefined =>
    cell.source === 'keyed' && row.hasDriver ? { color: 'var(--lodge-orange-500)' } : undefined;

  return (
    <>
      {/* Band row */}
      <tr className="fv3-r-stat-head bg-slate-50 border-t border-slate-200 hover:bg-slate-100 transition-colors">
        {isBulkMode && <td className="sticky left-0 bg-slate-50 py-1.5 px-2 z-20" />}
        <td className={`${isBulkMode ? '' : 'sticky left-0'} bg-slate-50 py-1.5 px-4 z-20`}>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); toggleSection(SECTION_KEY); }}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: 'var(--lodge-onyx-600)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-orange-500)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-600)'; }}
              title="Plan any stat account — covers, guests, comp rooms, labor hours, kWh — by keying months or assigning a driver. Room nights live under Occupancy."
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>Other Statistics</span>
            </button>
            {isExpanded && (isLoading || isFetching) && <Loader2 size={12} className="animate-spin text-slate-400" />}
            {isExpanded && data && rows.length > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-600" title={`${rows.length} stat accounts · ${drivenAccounts} driven · ${keyedAccounts} with keyed values`}>
                {drivenAccounts} driven · {keyedAccounts} keyed · {rows.length} accounts
              </span>
            )}
            {isExpanded && data?.tableReady === false && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700" title="Run `npm run db:push` in prisma/ to create forecastStatValue / forecastStatDriver">
                NOT DEPLOYED
              </span>
            )}
          </div>
        </td>
        {MONTH_LABELS.map((month, index) => {
          const isColExpanded = isColumnExpanded?.(index);
          if (isColExpanded) {
            return (
              <React.Fragment key={month}>
                {activeColumns.map((col) => (
                  <td key={col.key} className={`${col.isEditable ? '' : 'fv3-xcell '}py-1.5 px-2 bg-slate-50`} />
                ))}
              </React.Fragment>
            );
          }
          return <td key={month} className="py-1.5 px-3 bg-slate-50" />;
        })}
        <td colSpan={totalColSpan} className="py-1.5 px-4 bg-slate-50" />
      </tr>

      {isExpanded && error && (
        <tr className="fv3-r-stat bg-white">
          {isBulkMode && <td className="sticky left-0 bg-white z-20" />}
          <td className={stickyLabelClass}>
            <span className="text-red-600">Couldn't load statistics accounts</span>
          </td>
          <td colSpan={12 + totalColSpan} />
        </tr>
      )}

      {isExpanded && data && rows.length === 0 && (
        <tr className="fv3-r-stat bg-white">
          {isBulkMode && <td className="sticky left-0 bg-white z-20" />}
          <td className={stickyLabelClass}>
            <span className="text-slate-400 italic">No statistics accounts on this property's chart — stat-typed accounts appear here automatically.</span>
          </td>
          <td colSpan={12 + totalColSpan} />
        </tr>
      )}

      {isExpanded && rows.map((row, rowIdx) => {
        const rowSaving = row.cells.some((_, idx) => saving.has(`${row.account.chartOfAccountId}-${idx + 1}`));
        const chipText = describeStatDriver(row.account.driver, driverLabel(row.account.driver));
        const warnings = data?.warnings?.[row.account.chartOfAccountId] || [];
        const isSelected = selectedStatId === row.account.chartOfAccountId;
        const rowBg = isSelected ? 'bg-orange-50' : 'bg-white hover:bg-slate-50';
        return (
          <tr key={row.account.chartOfAccountId} className={`fv3-r-stat ${rowBg} transition-colors group`}>
            {isBulkMode && <td className={`sticky left-0 py-1 px-2 z-20 ${isSelected ? 'bg-orange-50' : 'bg-white group-hover:bg-slate-50'}`} />}
            <td
              className={`${isBulkMode ? '' : 'sticky left-0'} py-1 px-4 pl-8 text-xs text-slate-600 z-20 cursor-pointer ${isSelected ? 'bg-orange-50' : 'bg-white group-hover:bg-slate-50'}`}
              style={{ maxWidth: 360 }}
              onClick={() => selectStatAccount(isSelected ? null : row.account.chartOfAccountId)}
              title="Open the detail pane — driver, inputs and month-by-month math"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`truncate ${isSelected ? 'font-medium text-slate-800' : ''}`} title={`${row.account.accountNumber} · ${row.account.accountName}${row.account.department ? ` · ${row.account.department}` : ''}`}>
                  {row.account.accountName}
                </span>
                <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0">{row.account.accountNumber}</span>
                {row.account.categories.map((c) => (
                  <span key={c} className="px-1 py-0.5 rounded text-[9px] font-medium bg-sky-100 text-sky-700 flex-shrink-0" title={`Mapped as ${c} in Room Stats`}>{c}</span>
                ))}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); selectStatAccount(row.account.chartOfAccountId); }}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 transition-colors cursor-pointer ${
                    row.hasDriver ? 'bg-sky-50 text-sky-700 hover:bg-sky-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  } ${warnings.length ? 'ring-1 ring-amber-400' : ''}`}
                  title={warnings.length ? warnings.join(' · ') : `${chipText} — open the driver pane`}
                >
                  <Sliders size={10} />
                  <span className="truncate max-w-[160px]">{chipText}</span>
                </button>
                {canEdit && row.hasKeyed && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clearKeyed(row); }}
                    className="opacity-0 group-hover:opacity-100 text-[9px] font-medium px-1 py-0.5 rounded hover:bg-red-50 transition-opacity flex-shrink-0"
                    style={{ color: 'var(--lodge-orange-500)', border: '1px solid var(--lodge-orange-200)' }}
                    title={row.hasDriver ? 'Clear every keyed override on this account (back to the driver)' : 'Clear every keyed month on this account'}
                  >
                    Clear
                  </button>
                )}
                {rowSaving && <Loader2 size={10} className="animate-spin text-slate-400 flex-shrink-0" />}
              </div>
            </td>
            {MONTH_LABELS.map((month, monthIdx) => {
              const cell = row.cells[monthIdx];
              const isEditing = editing?.rowIdx === rowIdx && editing?.monthIdx === monthIdx;
              const isColExpanded = isColumnExpanded?.(monthIdx);
              const cellSaving = saving.has(`${row.account.chartOfAccountId}-${monthIdx + 1}`);
              const title = cellTitle(row, monthIdx);
              const overrideStar = cell.source === 'keyed' && row.hasDriver && cell.driven !== null
                ? <span className="fv3-star ml-0.5" style={{ color: 'var(--lodge-orange-400)' }} title={`Driver: ${formatStatNumber(cell.driven)}`}>*</span>
                : null;

              if (isColExpanded) {
                return (
                  <React.Fragment key={month}>
                    {activeColumns.map((col) => {
                      const isPlanCol = !!col.isEditable;
                      const val = isPlanCol ? cell.plan : statColumnValue(cell, col.key);
                      const isVarianceCol = col.colorMode === 'variance';
                      const editable = isPlanCol && canEdit;
                      const editingThis = isPlanCol && isEditing;
                      return (
                        <td
                          key={col.key}
                          className={`${isPlanCol ? '' : 'fv3-xcell '}text-right py-1 px-2 text-xs tabular-nums ${
                            isPlanCol ? valueClass(cell) : ''
                          } ${isVarianceCol && val !== null ? (val >= 0 ? 'text-emerald-600' : 'text-red-600') : isPlanCol ? '' : 'text-slate-600'} ${
                            editable ? 'cursor-pointer hover:bg-slate-100' : ''
                          } ${cellSaving ? 'opacity-50' : ''}`}
                          style={isPlanCol ? valueStyle(cell, row) : undefined}
                          onClick={() => editable && !editingThis && startEdit(rowIdx, monthIdx)}
                          title={isPlanCol ? title : undefined}
                        >
                          {editingThis ? renderInput(row, monthIdx) : (val === null ? <span className="text-slate-300">—</span> : <>{formatStatNumber(val)}{isPlanCol && overrideStar}</>)}
                        </td>
                      );
                    })}
                  </React.Fragment>
                );
              }

              return (
                <td
                  key={month}
                  className={`text-right py-1 px-3 text-xs tabular-nums ${valueClass(cell)} ${
                    canEdit ? 'cursor-pointer hover:bg-slate-100' : ''
                  } ${cellSaving ? 'opacity-50' : ''}`}
                  style={cell.source === 'actual' ? undefined : valueStyle(cell, row)}
                  onClick={() => canEdit && !isEditing && startEdit(rowIdx, monthIdx)}
                  title={title}
                >
                  {isEditing ? renderInput(row, monthIdx) : (
                    cell.display === null
                      ? <span className="text-slate-300">—</span>
                      : <>{formatStatNumber(cell.display)}{cell.source !== 'actual' && overrideStar}</>
                  )}
                </td>
              );
            })}
            {isColumnExpanded?.(TOTAL_COLUMN_INDEX) ? (
              activeColumns.map((col) => {
                const total = col.isEditable ? statColumnTotal(row.cells, 'forecast') : statColumnTotal(row.cells, col.key);
                const isVarianceCol = col.colorMode === 'variance';
                return (
                  <td key={`total-${col.key}`} className={`${col.isEditable ? '' : 'fv3-xcell '}text-right py-1 px-2 text-xs font-medium tabular-nums ${isVarianceCol && total !== null ? (total >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-700'}`}>
                    {total === null ? <span className="text-slate-300">—</span> : formatStatNumber(total)}
                  </td>
                );
              })
            ) : (
              <td className="text-right py-1 px-4 text-xs font-medium tabular-nums text-slate-700">
                {row.cells.every((c) => c.display === null) ? <span className="text-slate-300">—</span> : formatStatNumber(row.total)}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

export default OtherStatsSection;
